import { useState, useEffect } from "react";
import {
  ChemicalInventory,
  ChemicalProduct,
  Pool,
  Technician,
  ChemicalInventoryCreateRequest,
  ChemicalInventoryUpdateRequest,
  ChemicalUsageRequest,
  UsageResponse,
  inventoryApi,
  productApi,
  poolApi,
  technicianApi,
  PagedResponse
} from "../../services/poolService";
import { usePermissions } from "../../hooks/usePermissions";
import { toast } from "react-hot-toast";

interface InventoryFormData extends ChemicalInventoryCreateRequest {
  id?: number;
}

const InventoryContent = () => {
  const [inventory, setInventory] = useState<PagedResponse<ChemicalInventory> | null>(null);
  const [products, setProducts] = useState<ChemicalProduct[]>([]);
  const [pools, setPools] = useState<Pool[]>([]);
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUsageModalOpen, setIsUsageModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ChemicalInventory | null>(null);
  const [usageItem, setUsageItem] = useState<ChemicalInventory | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [filter, setFilter] = useState<"all" | "low-stock" | "warehouse">("all");

  const [formData, setFormData] = useState<InventoryFormData>({
    chemical_product_id: 0,
    initial_stock: 0,
    minimum_stock: 0,
    maximum_stock: 0,
    unit_cost: 0,
    location_description: "",
    notes: "",
  });

  const [usageData, setUsageData] = useState<ChemicalUsageRequest>({
    pool_id: 0,
    technician_id: 0,
    chemical_usages: [{
      chemical_product_id: 0,
      quantity_used: 0,
      notes: "",
    }],
    notes: "",
  });

  const { canReadInventory, canWriteInventory, canRegisterUsage } = usePermissions();

  useEffect(() => {
    if (canReadInventory) {
      loadData();
    }
  }, [canReadInventory, currentPage, filter]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load inventory based on filter
      let inventoryData: PagedResponse<ChemicalInventory> | ChemicalInventory[];

      if (filter === "low-stock") {
        const lowStockData = await inventoryApi.getLowStock();
        // Convert array to PagedResponse format
        inventoryData = {
          content: lowStockData,
          totalElements: lowStockData.length,
          totalPages: 1,
          number: 0,
          size: lowStockData.length,
          first: true,
          last: true,
          numberOfElements: lowStockData.length,
          empty: lowStockData.length === 0,
          pageable: {
            pageNumber: 0,
            pageSize: lowStockData.length,
            sort: { sorted: false, unsorted: true, empty: true },
            offset: 0,
            unpaged: false,
            paged: true,
          },
          sort: { sorted: false, unsorted: true, empty: true },
        };
      } else if (filter === "warehouse") {
        inventoryData = await inventoryApi.getCentralWarehouse(currentPage, 20, "chemicalProduct.name,asc");
      } else {
        inventoryData = await inventoryApi.getAll(currentPage, 20, "chemicalProduct.name,asc");
      }

      setInventory(inventoryData as PagedResponse<ChemicalInventory>);

      // Load products and pools for forms (only if user can write)
      if (canWriteInventory) {
        const [productsData, poolsData, techniciansData] = await Promise.all([
          productApi.getAll(),
          poolApi.getAll(),
          technicianApi.getAll(),
        ]);
        setProducts(productsData);
        setPools(poolsData);
        setTechnicians(techniciansData.filter(t => t.active));
      }
    } catch (error) {
      toast.error("Error al cargar inventario");
      console.error("Error loading inventory:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingItem(null);
    setFormData({
      chemical_product_id: 0,
      initial_stock: 0,
      minimum_stock: 0,
      maximum_stock: 0,
      unit_cost: 0,
      location_description: "",
      notes: "",
    });
    setIsModalOpen(true);
  };

  const handleEdit = (item: ChemicalInventory) => {
    setEditingItem(item);
    setFormData({
      id: item.id,
      chemical_product_id: item.chemical_product.id,
      pool_id: item.pool?.id,
      initial_stock: item.current_stock,
      minimum_stock: item.minimum_stock,
      maximum_stock: item.maximum_stock,
      unit_cost: item.unit_cost,
      location_description: item.location_description || "",
      notes: "",
    });
    setIsModalOpen(true);
  };

  const handleUsage = (item: ChemicalInventory) => {
    setUsageItem(item);
    setUsageData({
      pool_id: item.pool?.id || 0,
      technician_id: 0,
      chemical_usages: [{
        chemical_product_id: item.chemical_product.id,
        quantity_used: 0,
        notes: "",
      }],
      notes: "",
    });
    setIsUsageModalOpen(true);
  };

  const handleDelete = async (id: number, productName: string) => {
    if (!canWriteInventory) {
      toast.error("No tienes permisos para eliminar items de inventario");
      return;
    }

    if (window.confirm(`¿Estás seguro de que deseas eliminar "${productName}" del inventario?`)) {
      try {
        await inventoryApi.delete(id);
        toast.success("Item eliminado del inventario exitosamente");
        loadData();
      } catch (error) {
        toast.error("Error al eliminar item del inventario");
        console.error("Error deleting inventory item:", error);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!canWriteInventory) {
      toast.error("No tienes permisos para gestionar inventario");
      return;
    }

    try {
      if (editingItem) {
        const updateData: ChemicalInventoryUpdateRequest = {
          current_stock: formData.initial_stock,
          minimum_stock: formData.minimum_stock,
          maximum_stock: formData.maximum_stock || undefined,
          unit_cost: formData.unit_cost || undefined,
          location_description: formData.location_description || undefined,
        };
        await inventoryApi.update(editingItem.id, updateData);
        toast.success("Item de inventario actualizado exitosamente");
      } else {
        // Limpiar campos opcionales con valor 0 antes de enviar al backend
        const createPayload: ChemicalInventoryCreateRequest = {
          chemical_product_id: formData.chemical_product_id,
          pool_id: formData.pool_id || undefined,
          initial_stock: formData.initial_stock,
          minimum_stock: formData.minimum_stock,
          maximum_stock: formData.maximum_stock || undefined,
          unit_cost: formData.unit_cost || undefined,
          location_description: formData.location_description || undefined,
          notes: formData.notes || undefined,
        };
        await inventoryApi.create(createPayload);
        toast.success("Item de inventario creado exitosamente");
      }

      setIsModalOpen(false);
      loadData();
    } catch (error) {
      toast.error(`Error al ${editingItem ? 'actualizar' : 'crear'} el item de inventario`);
      console.error("Error saving inventory item:", error);
    }
  };

  const handleUsageSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!canRegisterUsage) {
      toast.error("No tienes permisos para registrar uso de químicos");
      return;
    }

    try {
      const result: UsageResponse = await inventoryApi.registerUsage(usageData);
      const warnings = result.affected_inventories.filter(a => a.low_stock_warning);
      if (warnings.length > 0) {
        toast(`⚠️ Uso registrado. Stock bajo en: ${warnings.map(w => w.product_name).join(', ')}`, {
          icon: '⚠️',
          duration: 5000,
        });
      } else {
        toast.success(`Uso registrado: ${result.movements_created} movimiento(s)`);
      }
      setIsUsageModalOpen(false);
      loadData();
    } catch (error) {
      toast.error("Error al registrar uso de químico");
      console.error("Error registering usage:", error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'chemical_product_id' || name === 'pool_id'
        ? parseInt(value) || undefined
        : name.includes('stock') || name === 'unit_cost'
        ? value === '' ? 0 : parseFloat(value)
        : value,
    }));
  };

  const handleUsageInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    if (name === 'quantity_used') {
      setUsageData(prev => ({
        ...prev,
        chemical_usages: [{
          ...prev.chemical_usages[0],
          quantity_used: value === '' ? 0 : parseFloat(value),
        }]
      }));
    } else if (name === 'technician_id') {
      setUsageData(prev => ({
        ...prev,
        technician_id: parseInt(value) || 0,
      }));
    } else {
      setUsageData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const getStockStatusBadge = (status: string) => {
    const badges = {
      OUT_OF_STOCK: "bg-red-100 text-red-800",
      CRITICAL_LOW: "bg-red-100 text-red-800",
      LOW_STOCK: "bg-yellow-100 text-yellow-800",
      NORMAL: "bg-green-100 text-green-800",
      OVER_STOCK: "bg-blue-100 text-blue-800",
    };
    return badges[status as keyof typeof badges] || "bg-gray-100 text-gray-800";
  };

  if (!canReadInventory) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Acceso Denegado</h1>
          <p className="text-gray-600">No tienes permisos para ver esta página.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Inventario de Químicos</h1>
            <p className="mt-2 text-sm text-gray-700">
              Gestiona el inventario de productos químicos para el mantenimiento de piscinas
            </p>
          </div>

          {/* Filters and Actions */}
          <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
            <div className="flex space-x-2">
              <button
                onClick={() => setFilter("all")}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  filter === "all"
                    ? "bg-teal-600 text-white"
                    : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                }`}
              >
                Todos
              </button>
              <button
                onClick={() => setFilter("low-stock")}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  filter === "low-stock"
                    ? "bg-teal-600 text-white"
                    : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                }`}
              >
                Stock Bajo
              </button>
              <button
                onClick={() => setFilter("warehouse")}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  filter === "warehouse"
                    ? "bg-teal-600 text-white"
                    : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                }`}
              >
                Almacén Central
              </button>
            </div>

            {canWriteInventory && (
              <button
                onClick={handleCreate}
                className="bg-teal-600 text-white px-6 py-2 rounded-md hover:bg-teal-700 transition-colors"
              >
                ➕ Agregar Item
              </button>
            )}
          </div>

          {/* Inventory Table */}
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">Cargando inventario...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Producto Químico
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ubicación
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Stock Actual
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Stock Min/Max
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Estado
                      </th>
                      {/* Removed Valor Total th header */}
                      {(canWriteInventory || canRegisterUsage) && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Acciones
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {inventory?.content && Array.isArray(inventory.content) ? inventory.content.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {item.chemical_product.icon && (
                              <img
                                className="h-8 w-8 rounded-full mr-3"
                                src={`/icons/${item.chemical_product.icon}`}
                                alt={item.chemical_product.name}
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                            )}
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {item.chemical_product.name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {item.chemical_product.productId}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {item.pool ? item.pool.name : "Almacén Central"}
                          </div>
                          {item.location_description && (
                            <div className="text-sm text-gray-500">
                              {item.location_description}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.current_stock.toFixed(3)} {item.chemical_product.unit}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.minimum_stock.toFixed(1)} / {item.maximum_stock.toFixed(1)} {item.chemical_product.unit}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStockStatusBadge(item.stock_status)}`}
                          >
                            {item.stock_status === 'LOW_STOCK' || item.stock_status === 'CRITICAL_LOW' ? 'Stock Bajo' :
                             item.stock_status === 'OVER_STOCK' ? 'Stock Alto' :
                             item.stock_status === 'OUT_OF_STOCK' ? 'Sin Stock' : 'Normal'}
                          </span>
                        </td>

                        {(canWriteInventory || canRegisterUsage) && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              {canWriteInventory && (
                                <>
                                  <button
                                    onClick={() => handleEdit(item)}
                                    className="text-teal-600 hover:text-teal-900"
                                  >
                                    ✏️ Editar
                                  </button>
                                  <button
                                    onClick={() => handleDelete(item.id, item.chemical_product.name)}
                                    className="text-red-600 hover:text-red-900"
                                  >
                                    🗑️ Eliminar
                                  </button>
                                </>
                              )}
                              {canRegisterUsage && (
                                <button
                                  onClick={() => handleUsage(item)}
                                  className="text-blue-600 hover:text-blue-900"
                                >
                                  📝 Uso
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                          No hay items en el inventario
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {inventory && inventory.totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex justify-between flex-1 sm:hidden">
                  <button
                    onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                    disabled={inventory.first}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(inventory.totalPages - 1, currentPage + 1))}
                    disabled={inventory.last}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Siguiente
                  </button>
                </div>
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Mostrando{' '}
                      <span className="font-medium">{inventory.number * inventory.size + 1}</span>
                      {' '}a{' '}
                      <span className="font-medium">
                        {Math.min((inventory.number + 1) * inventory.size, inventory.totalElements)}
                      </span>
                      {' '}de{' '}
                      <span className="font-medium">{inventory.totalElements}</span>
                      {' '}resultados
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                        disabled={inventory.first}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Anterior
                      </button>
                      <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                        {currentPage + 1} / {inventory.totalPages}
                      </span>
                      <button
                        onClick={() => setCurrentPage(Math.min(inventory.totalPages - 1, currentPage + 1))}
                        disabled={inventory.last}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Siguiente
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingItem ? "Editar Item de Inventario" : "Nuevo Item de Inventario"}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Producto Químico
                  </label>
                  <select
                    name="chemical_product_id"
                    value={formData.chemical_product_id}
                    onChange={handleInputChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                    required
                  >
                    <option value={0}>Seleccionar producto</option>
                    {products.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.name} ({product.productId})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Piscina (Opcional)
                  </label>
                  <select
                    name="pool_id"
                    value={formData.pool_id || ''}
                    onChange={handleInputChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                  >
                    <option value="">Almacén Central</option>
                    {pools.map((pool) => (
                      <option key={pool.id} value={pool.id}>
                        {pool.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Stock Inicial
                  </label>
                  <input
                    type="number"
                    name="initial_stock"
                    value={formData.initial_stock || ''}
                    onChange={handleInputChange}
                    step="0.001"
                    min="0"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                    placeholder="0.000"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Stock Mínimo
                    </label>
                    <input
                      type="number"
                      name="minimum_stock"
                      value={formData.minimum_stock || ''}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                      placeholder="0.00"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Stock Máximo
                    </label>
                    <input
                      type="number"
                      name="maximum_stock"
                      value={formData.maximum_stock || ''}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                      placeholder="0.00"
                      required
                    />
                  </div>
                </div>


                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Descripción de Ubicación
                  </label>
                  <input
                    type="text"
                    name="location_description"
                    value={formData.location_description}
                    onChange={handleInputChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                    placeholder="Ej: Estante A, Nivel 2"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-md hover:bg-teal-700"
                  >
                    {editingItem ? "Actualizar" : "Crear"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {isUsageModalOpen && usageItem && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Registrar Uso de Químico
              </h3>
              <div className="mb-4 p-3 bg-gray-50 rounded-md">
                <p className="text-sm font-medium text-gray-900">{usageItem.chemical_product.name}</p>
                <p className="text-sm text-gray-600">
                  Stock actual: {usageItem.current_stock.toFixed(3)} {usageItem.chemical_product.unit}
                </p>
              </div>
              <form onSubmit={handleUsageSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Cantidad a Usar
                  </label>
                  <input
                    type="number"
                    name="quantity_used"
                    value={usageData.chemical_usages[0]?.quantity_used || ''}
                    onChange={handleUsageInputChange}
                    step="0.001"
                    min="0.001"
                    max={usageItem?.current_stock || 0}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                    placeholder="0.000"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Técnico
                  </label>
                  <select
                    name="technician_id"
                    value={usageData.technician_id}
                    onChange={handleUsageInputChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                    required
                  >
                    <option value={0}>Seleccionar técnico</option>
                    {technicians.map((tech) => (
                      <option key={tech.id} value={tech.id}>
                        {tech.name} — {tech.specialty}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Notas (opcional)
                  </label>
                  <textarea
                    name="notes"
                    value={usageData.notes}
                    onChange={handleUsageInputChange}
                    rows={3}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                    placeholder="Detalles del uso..."
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setIsUsageModalOpen(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-md hover:bg-teal-700"
                  >
                    Registrar Uso
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InventoryContent;